import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Input } from '@/components/ui'
import { SEO } from '@/components/seo'
import { GradientHero } from '@/components/layout'
import { usePricingTiers } from '@/hooks/usePricingTiers'
import { useUtmPassthrough } from '@/hooks/useUtmPassthrough'
import { useContactForm } from '@/hooks/useContactForm'
import { appSignupLink } from '@/utils/signupUrl'
import { USE_APP_SIGNUP } from '@/utils/featureFlags'
import { trackSignupCtaClick } from '@/utils/rum'
import { testimonials } from '@/data/journeys'
import type { PricingTier } from '@/types/pricing'

const TRUST_BADGES = ['For FCA-regulated firms', 'Built to UK GDPR standards', 'AES-256 encryption', 'UK-hosted data']

function PlanRadio({
  tier,
  selected,
  onSelect,
}: {
  tier: PricingTier
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      role="radio"
      aria-checked={selected}
      className={`w-full text-left bg-white border rounded-xl p-4 transition-all duration-200 cursor-pointer ${
        selected
          ? 'border-accent-teal ring-2 ring-accent-teal/30'
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-base font-semibold text-slate-900">{tier.displayName}</h3>
            {tier.popular && (
              <span className="text-[10px] font-bold tracking-wider uppercase text-accent-teal bg-teal-50 px-1.5 py-0.5 rounded">
                Popular
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 leading-snug">{tier.description}</p>
        </div>
        <div className="text-right flex-shrink-0">
          {tier.monthlyPrice !== null ? (
            <>
              <div className="font-serif text-xl text-slate-900 leading-none">
                £{tier.monthlyPrice}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">/adviser/mo</div>
            </>
          ) : (
            <div className="font-serif text-base text-slate-900">Custom</div>
          )}
        </div>
      </div>
    </button>
  )
}

export default function SignUpPageV2() {
  const [searchParams] = useSearchParams()
  const { tiers } = usePricingTiers()
  const utm = useUtmPassthrough()
  const incomingPlan = searchParams.get('plan')

  // Default to the popular tier if nothing is specified.
  const defaultSlug =
    tiers.find((t) => t.slug === incomingPlan)?.slug ??
    tiers.find((t) => t.popular)?.slug ??
    tiers[0]?.slug ??
    null

  const { formData, isSubmitting, isSubmitted, error, handleChange, setField, handleSubmit } =
    useContactForm('signup')

  // Pre-select plan in form state so it travels with the submission.
  const selectedSlug = formData.plan || defaultSlug
  const selectedTier = tiers.find((t) => t.slug === selectedSlug) ?? null

  useEffect(() => {
    if (defaultSlug && !formData.plan) setField('plan', defaultSlug)
  }, [defaultSlug, formData.plan, setField])

  // When the app's /signup endpoint is live, the brochure /sign-up should hand
  // over immediately rather than render. Do it as a side effect so we never
  // flash form content before the redirect.
  useEffect(() => {
    if (!USE_APP_SIGNUP) return
    const link = appSignupLink({ plan: selectedSlug ?? undefined, params: utm })
    window.location.assign(link.href)
  }, [selectedSlug, utm])

  if (USE_APP_SIGNUP) {
    return (
      <section className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 text-center">
        <div className="max-w-[400px]">
          <div className="w-12 h-12 rounded-full border-2 border-slate-200 border-t-accent-teal animate-spin mx-auto mb-6" />
          <p className="text-slate-600">Taking you to setup…</p>
        </div>
      </section>
    )
  }

  const handlePlanSelect = (tier: PricingTier) => {
    if (tier.isContactSales) {
      window.open('mailto:support@wealthspace.ai?subject=Enterprise enquiry', '_self')
      return
    }
    setField('plan', tier.slug)
  }

  const onSubmit = (e: React.FormEvent) => {
    trackSignupCtaClick({
      location: 'pricing_card',
      plan: selectedSlug ?? undefined,
      hasUtm: Object.keys(utm).length > 0,
    })
    handleSubmit(e)
  }

  return (
    <>
      <SEO
        title="Get started | WealthSpace"
        description="Pick a plan, add your details, and log straight in. Your account spins up on the spot for your card-secured 7-day trial."
        url="/sign-up"
      />

      <GradientHero
        revisionBadge="Card-secured 7-day trial"
        title={
          isSubmitted ? (
            <>
              You&apos;re <span className="italic text-teal-200">in</span>
            </>
          ) : (
            <>
              Get started <span className="italic text-teal-200">now</span>
            </>
          )
        }
        subtitle={
          isSubmitted
            ? 'Your account is live. Login details are on their way: your card-secured 7-day trial starts the moment you sign in.'
            : 'Pick a plan and add your details. Your account spins up on the spot, ready for you to start your trial.'
        }
      />

      <section className="px-6 py-16">
        <div className="max-w-[1100px] mx-auto">
          {!isSubmitted ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
              {/* LEFT: plan + form */}
              <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-card">
                <form onSubmit={onSubmit} className="space-y-8">
                  {/* Plan selector */}
                  <fieldset>
                    <legend className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-3">
                      Choose your starting plan
                    </legend>
                    <div role="radiogroup" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {tiers.map((tier) => (
                        <PlanRadio
                          key={tier.slug}
                          tier={tier}
                          selected={selectedSlug === tier.slug}
                          onSelect={() => handlePlanSelect(tier)}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-3">
                      You can change tier any time once your account is live.
                    </p>
                  </fieldset>

                  {/* Details */}
                  <div className="space-y-4">
                    <p className="text-xs font-bold tracking-wider uppercase text-slate-500">
                      Your details
                    </p>
                    <Input
                      label="Work email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@yourfirm.co.uk"
                      autoComplete="email"
                    />
                    <Input
                      label="Firm name"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Firm Ltd"
                      autoComplete="organization"
                    />
                    <Input
                      label="Your name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      autoComplete="name"
                    />
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="hidden"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-rose-600" role="alert">
                      {error}
                    </p>
                  )}

                  <div>
                    <Button
                      type="submit"
                      disabled={isSubmitting || !selectedTier}
                      className="w-full justify-center"
                      size="lg"
                    >
                      {isSubmitting
                        ? 'Setting you up…'
                        : selectedTier
                          ? `Get started with ${selectedTier.displayName}`
                          : 'Get started'}
                    </Button>
                    <p className="text-xs text-slate-400 text-center mt-3">
                      By continuing you agree to our{' '}
                      <a href="/terms" className="text-accent-teal hover:underline">
                        Terms
                      </a>{' '}
                      and{' '}
                      <a href="/privacy-policy" className="text-accent-teal hover:underline">
                        Privacy Policy
                      </a>
                      . Your card-secured trial begins as soon as your account is live.
                    </p>
                  </div>
                </form>
              </div>

              {/* RIGHT: what happens next + trust */}
              <aside className="space-y-5">
                {/* What happens next */}
                <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6">
                  <p className="text-xs font-bold tracking-wider uppercase text-accent-teal mb-4">
                    What happens next
                  </p>
                  <ol className="space-y-4">
                    {[
                      'Pick your starting plan and add your details.',
                      'Account live on the spot. Login emailed instantly.',
                      'Card-secured 7-day trial starts now. Auto-converts to paid on day 7, cancel any time before then.',
                    ].map((step, i) => (
                      <li key={step} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-teal text-white text-xs font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Testimonial */}
                {testimonials[0] && (
                  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-card">
                    <svg className="w-7 h-7 text-accent-teal/30 mb-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
                    </svg>
                    <p className="text-sm text-slate-700 italic leading-relaxed mb-4">
                      &ldquo;{testimonials[0].quote}&rdquo;
                    </p>
                    <div className="text-xs">
                      <p className="font-semibold text-slate-900">{testimonials[0].author}</p>
                      <p className="text-slate-500">
                        {testimonials[0].role}
                        {testimonials[0].company ? `, ${testimonials[0].company}` : ''}
                      </p>
                    </div>
                  </div>
                )}

                {/* Trust badges */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-card">
                  <p className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-4">
                    Built for UK advice firms
                  </p>
                  <ul className="space-y-3">
                    {TRUST_BADGES.map((badge) => (
                      <li key={badge} className="flex items-center gap-3 text-sm text-slate-700">
                        <span className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3.5 h-3.5 text-accent-teal" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        <span>{badge}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                    Monthly rolling. Cancel from your account settings, no exit fees, no awkward calls.
                  </p>
                </div>
              </aside>
            </div>
          ) : (
            // Inline confirmation: same layout, just success state.
            <div className="max-w-[640px] mx-auto bg-white border border-slate-200 rounded-2xl p-10 shadow-card text-center">
              <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-6">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0f766e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-serif text-slate-900 mb-3">
                {selectedTier ? `Your ${selectedTier.displayName} account is live` : 'Your account is live'}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Check your inbox: your login link from <strong>team@wealthspace.ai</strong> should already be there.
                Sign in to start your card-secured 7-day trial.
                {selectedTier?.monthlyPrice !== null && selectedTier?.monthlyPrice !== undefined && (
                  <> Plan: <strong>{selectedTier.displayName}</strong> · £{selectedTier.monthlyPrice}/adviser/month, billed monthly after the trial auto-converts on day 7.</>
                )}
              </p>
              <Button href="/" variant="secondary">
                Back to Home
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
